import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { STATUS_LABEL, formatTurkishDate } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { useInvitationViewer } from "@/contexts/InvitationViewerContext";
import CategoryIcon from "@/components/CategoryIcon";
import { Image as ImageIcon } from "lucide-react";

const statusStyles = {
  approved: "bg-[#4A5D23]/12 text-[#4A5D23] border-[#4A5D23]/30",
  pending: "bg-[#D97706]/12 text-[#D97706] border-[#D97706]/30",
  rejected: "bg-[#7F1D1D]/12 text-[#7F1D1D] border-[#7F1D1D]/30",
};

const EventsTable = ({ events = [], actions, emptyText = "Henüz etkinlik yok" }) => {
  const { categoryMap } = useSettings();
  const { openInvitation } = useInvitationViewer();

  if (events.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-10 text-center animate-fade-up" data-testid="events-table-empty">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold mb-2">Etkinlikler</div>
        <p className="text-[#4B5563]">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden animate-fade-up" data-testid="events-table">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9F6F0] hover:bg-[#F9F6F0]">
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-[#8A252C]/80 font-bold">Kategori</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-[#8A252C]/80 font-bold">Tarih</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-[#8A252C]/80 font-bold">Kimin</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-[#8A252C]/80 font-bold">Aileler</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-[#8A252C]/80 font-bold">Mekan</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-[#8A252C]/80 font-bold">Bildiren</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-[#8A252C]/80 font-bold">Durum</TableHead>
              {actions && (
                <TableHead className="text-[11px] uppercase tracking-[0.15em] text-[#8A252C]/80 font-bold text-right">İşlem</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((ev) => {
              const cat = categoryMap[ev.category];
              return (
                <TableRow key={ev.id} data-testid={`event-row-${ev.id}`} className="hover:bg-[#F9F6F0]/60 transition-colors">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md flex items-center justify-center text-xs"
                        style={{ backgroundColor: `${cat?.accent}15`, color: cat?.accent }}>
                        <CategoryIcon icons={cat?.icons} />
                      </span>
                      <span className="font-medium text-[#1F2937]">{cat?.label || ev.category}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-[#4B5563] whitespace-nowrap">{formatTurkishDate(ev.event_datetime)}</TableCell>
                  <TableCell className="font-medium text-[#1F2937]">{ev.whose_event}</TableCell>
                  <TableCell className="text-sm text-[#4B5563]">{ev.families}</TableCell>
                  <TableCell className="text-sm text-[#4B5563]">
                    <div>{ev.venue}</div>
                    {ev.invitation_url && (
                      <button
                        type="button"
                        onClick={() => openInvitation(ev.invitation_url)}
                        data-testid={`table-invitation-${ev.id}`}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#8A252C] hover:underline"
                      >
                        <ImageIcon className="w-3 h-3" />
                        Davetiyeyi Gör
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-[#1F2937]">{ev.notifier_name}</div>
                    <div className="text-xs text-[#4B5563]">{ev.contact_number}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`${statusStyles[ev.status]} font-semibold`} data-testid={`event-status-${ev.id}`}>
                      {STATUS_LABEL[ev.status]}
                    </Badge>
                  </TableCell>
                  {actions && (<TableCell className="text-right">{actions(ev)}</TableCell>)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default EventsTable;
